/**
 * Persist the last selected integration per user and UI scope (Connector, Admin, etc.).
 */

export type SelectedIntegrationScope = 'connector' | 'admin';

const STORAGE_KEY_PREFIX = 'infiano_selected_integration_';

function storageKey(scope: SelectedIntegrationScope, userId?: string | null): string {
  const userPart = userId ? String(userId) : 'anonymous';
  return `${STORAGE_KEY_PREFIX}${scope}_${userPart}`;
}

export function getIntegrationRecordId(integration: { id?: string; _id?: string }): string {
  return String(integration?.id || integration?._id || '');
}

export function integrationExistsInList(
  integrations: Array<{ id?: string; _id?: string }>,
  integrationId: string
): boolean {
  if (!integrationId) return false;
  return integrations.some((item) => getIntegrationRecordId(item) === integrationId);
}

export function getStoredSelectedIntegrationId(
  scope: SelectedIntegrationScope,
  userId?: string | null
): string | null {
  try {
    const raw = localStorage.getItem(storageKey(scope, userId));
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

export function setStoredSelectedIntegrationId(
  scope: SelectedIntegrationScope,
  userId: string | null | undefined,
  integrationId: string | null
): void {
  try {
    const key = storageKey(scope, userId);
    if (!integrationId) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, integrationId);
  } catch {
    // ignore quota / private mode
  }
}

export function clearStoredSelectedIntegrationId(
  scope: SelectedIntegrationScope,
  userId?: string | null
): void {
  setStoredSelectedIntegrationId(scope, userId, null);
}

/**
 * Pick a stored integration id if it still exists in the list; otherwise clear stale storage.
 */
export function resolveStoredIntegrationId(
  scope: SelectedIntegrationScope,
  userId: string | null | undefined,
  integrations: Array<{ id?: string; _id?: string }>
): string | null {
  const stored = getStoredSelectedIntegrationId(scope, userId);
  if (!stored) return null;
  if (integrationExistsInList(integrations, stored)) return stored;
  clearStoredSelectedIntegrationId(scope, userId);
  return null;
}
